import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import {
  fetchCurrentIdentity,
  type CurrentOrganizationRole,
} from '@/auth/currentIdentity';
import { useRuntimeAuthSdk, type Workspace } from '@/auth/runtimeAuth';
import { fetchWithSession } from '@/auth/sessionFetch';
import { isDemoMode } from '@/lib/demoMode';
import { logger } from '@/lib/logger';
import * as chatPrefs from '@/utils/chatPrefs';
import { useDemoAuth } from './useDemoAuth';

export interface AuthOrganization {
  id: string;
  role: CurrentOrganizationRole;
}

/**
 * Who the provider session says you are, held synchronously by the auth SDK.
 * Available on first paint, unlike canonical identity.
 */
export interface AuthProfile {
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
}

export interface AuthUser extends AuthProfile {
  id: string;
  organization: AuthOrganization;
  [key: string]: unknown;
}

/** Best available name to show for a profile, if there is one at all. */
export function displayName(profile: AuthProfile): string | undefined {
  return profile.name ?? profile.email?.split('@')[0];
}

export interface AuthContextValue {
  user: AuthUser | null;
  /**
   * Display fields, straight off the auth SDK and therefore populated on first
   * paint. Read these for anything the user merely looks at — a greeting, a
   * header, a profile form. `user` carries the same fields but only once
   * `/auth/current` lands, so sourcing display copy from it leaves the UI blank
   * for a round trip (ENG-1316). Read `user` when you need backend-confirmed
   * identity or organization authorization.
   */
  profile: AuthProfile;
  /**
   * The auth SDK has not hydrated yet, so nothing about the session is knowable
   * — not even whether one exists. Route guards must defer while this is true;
   * see the auth section of the repo AGENTS.md.
   */
  isLoading: boolean;
  /**
   * A provider session exists. Read synchronously from the hydrated SDK, so it
   * settles with `isLoading` rather than a round trip behind it — this is what
   * lets the app shell paint before `/auth/current` resolves (ENG-1316). It
   * means "the browser holds a session", NOT "the backend confirmed this
   * identity"; anything that needs a backend-confirmed verdict must read
   * `user`, which stays null until identity lands.
   */
  isAuthenticated: boolean;
  /**
   * A session exists but `GET /auth/current` has not answered yet, so `user`
   * is null because we don't know rather than because there is nobody.
   * Consumers that would otherwise render "absent" for a pending value (an
   * organization panel, a seeded form) should show a pending state instead.
   */
  isIdentityPending: boolean;
  isEmailVerified: boolean;
  /**
   * This session has a profile worth opening — so the account/session cluster
   * is worth rendering at all. True in demo, where `/account` shows a real
   * profile form, theme and language pickers, and the mocked team.
   *
   * A capability on the session rather than an `isDemoMode()` branch in each
   * consumer: `useAuth` already swaps its whole implementation per mode, so
   * this is where the answer is known.
   */
  canManageSession: boolean;
  /**
   * This session can actually be ended. False in demo, where `logout` would
   * return you signed in as the same static user.
   *
   * Split from `canManageSession` because the two stopped agreeing once the
   * demo got an account page: the demo has a profile to open but no session to
   * revoke,
   * and one flag gating both meant unlocking the account page would also ship a
   * logout button that ignores the click. Consumers render logout `disabled`
   * rather than hiding it, so the control the real app has stays visible and
   * explains itself.
   */
  canEndSession: boolean;
  /**
   * This session can be moved to another workspace the person belongs to.
   * False in demo and for the consumer provider, whose sessions belong to no
   * organization; consumers hide the control. See
   * `RuntimeAuthSdk.canSwitchWorkspace` for why a switch is a new session.
   */
  canSwitchWorkspace: boolean;
  listWorkspaces: () => Promise<Workspace[]>;
  /**
   * Exchange this session for one in `workspaceId` and reload the app on it.
   * Never resolves on success: the old session's client state is torn down
   * exactly as `logout` tears it down, then the document navigates — to `/`
   * with the new session, or to `/login` when the provider stopped at a step
   * this app has no screen for (`WorkspaceSwitchOutcome`). Rejects, with
   * nothing torn down, when the exchange itself fails.
   */
  switchWorkspace: (workspaceId: string) => Promise<void>;
  error: Error | null;
  retry: () => void;
  logout: () => Promise<void>;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

function useRuntimeAuth(): AuthContextValue {
  const sdk = useRuntimeAuthSdk();
  const { subject } = sdk;
  const queryClient = useQueryClient();

  // One condition, read twice below. A disabled query reports `isPending: true`
  // forever, so a pending flag derived from anything narrower than the query's
  // own `enabled` would pin true and leave every placeholder that reads it
  // stuck (see `TeamManagement`).
  const identityEnabled = sdk.isInitialized && subject !== null;

  const identityQuery = useQuery({
    // The scalar id, never the object: `subject` is rebuilt off the Stytch
    // profile, so keying on it would refetch identity on a name edit. The id
    // must stay — it stops one member's cached org role serving the next
    // within `staleTime`, and a B2B member can change organization in place.
    queryKey: ['auth', 'current', subject?.id],
    queryFn: () =>
      fetchCurrentIdentity({
        getSessionJwt: sdk.getSessionJwt,
        refreshSession: sdk.refreshSession,
      }),
    enabled: identityEnabled,
    retry: false,
    staleTime: 60_000,
  });

  // With nothing cached, a refetch of an errored query goes back to
  // `status: 'pending'`, so a retry is observable through `isPending`;
  // `isFetching` keeps it so should that library behavior change.
  const isIdentityPending =
    identityEnabled &&
    (identityQuery.isPending || identityQuery.isFetching) &&
    !identityQuery.data;

  // Only SDK hydration blocks the app. It is synchronous once it lands (it
  // reads storage), and a subject is enough to pick a shell — app or login.
  // Canonical identity fills in behind the paint instead of gating it.
  //
  // These two must be re-cut together, never one at a time: `isLoading: false`
  // with `isAuthenticated: false` reads as "signed out" to every route guard,
  // so narrowing `isLoading` alone would bounce a live session to /login (the
  // regression `_app.tsx`'s defer exists to prevent). Deriving the second from
  // the first keeps "never authenticated while loading" true by construction —
  // `isInitialized` is `sessionInitialized && userInitialized` and those land
  // independently, so a half-hydrated SDK can report a session already.
  const isLoading = !sdk.isInitialized;
  const isAuthenticated = !isLoading && subject !== null;

  const email = subject?.email;
  const name = subject?.name;
  const firstName = subject?.firstName;
  const lastName = subject?.lastName;
  const profile = useMemo<AuthProfile>(
    () => ({ email, name, firstName, lastName }),
    [email, firstName, lastName, name],
  );

  const user = useMemo<AuthUser | null>(() => {
    const identity = identityQuery.data;
    if (!identity) return null;
    return {
      id: identity.user_id,
      organization: {
        id: identity.organization.org_id,
        role: identity.organization.org_role,
      },
      ...profile,
    };
  }, [identityQuery.data, profile]);

  // Callers pass a ready `BodyInit` — the one production caller, the AG-UI
  // agent transport, always sends a JSON string with its own Content-Type.
  // Typed `RequestInit` so an object body is a compile error, not a silent
  // `[object Object]`.
  const authFetch = useCallback(
    (url: string, options: RequestInit = {}): Promise<Response> =>
      fetchWithSession(url, options, {
        getSessionJwt: sdk.getSessionJwt,
        refreshSession: sdk.refreshSession,
      }),
    [sdk],
  );

  const error =
    identityQuery.error instanceof Error ? identityQuery.error : null;

  // A refetch that fails while usable identity is already cached renders
  // nothing — `main.tsx` only banners when `user` is null, because the app is
  // fully functional on the retained data and a permanent bar over the header
  // would be a false alarm. This log is the only trace that window leaves.
  useEffect(() => {
    if (error) logger.warn('[Auth] canonical identity fetch failed', { error });
  }, [error]);

  // Everything this browser holds about the current session, other than the
  // provider's own tokens. One place, because a workspace switch is a session
  // boundary too: the B2B member id changes with the organization, so the next
  // boot identifies a different person to PostHog, and the chat
  // session id belongs to the workspace being left (/login clears it as well,
  // for the paths that end there without passing through here).
  const endSessionLocally = useCallback(() => {
    posthog.reset();
    queryClient.removeQueries({ queryKey: ['auth', 'current'] });
    chatPrefs.clearOnLogout();
  }, [queryClient]);

  const logout = useCallback(async () => {
    posthog.capture('user_logged_out');
    endSessionLocally();
    try {
      await sdk.revokeSession();
    } catch (err) {
      logger.error('Stytch logout error', { error: err });
    }
    window.location.assign('/login');
  }, [endSessionLocally, sdk]);

  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      // The exchange first, and teardown only once it has answered: a refused
      // exchange leaves the person exactly where they were.
      const outcome = await sdk.switchWorkspace(workspaceId);
      if (outcome === 'switched') posthog.capture('workspace_switched');
      endSessionLocally();
      window.location.assign(outcome === 'switched' ? '/' : '/login');
    },
    [endSessionLocally, sdk],
  );

  return {
    user,
    profile,
    isLoading,
    isAuthenticated,
    isIdentityPending,
    isEmailVerified: sdk.isEmailVerified,
    // A real provider session: there is a profile to edit and a session to end.
    canManageSession: true,
    canEndSession: true,
    canSwitchWorkspace: sdk.canSwitchWorkspace,
    listWorkspaces: sdk.listWorkspaces,
    switchWorkspace,
    error,
    retry: () => {
      void identityQuery.refetch();
    },
    logout,
    authFetch,
  };
}

// Module-scope selection keeps rules-of-hooks intact: session stays demo or real.
export const useAuth: () => AuthContextValue = isDemoMode()
  ? useDemoAuth
  : useRuntimeAuth;
